import { encodePreference, isPreferenceKey } from '@/config/preferences'
import {
  PermissionsCatalog,
  PermissionGrant
} from '@/features/application/applicationTypes'
import type {
  GlobalSearchResponse,
  SearchScope
} from '@/features/internal/search/searchTypes'
import type { PreferencesSummary } from '@/server/preferences/summary'
import { baseApi } from '@/stores/api/base-api'

export type AuthMeResponse = {
  /**
   * Whose grants these are. The cache key carries no user, so this is what
   * lets usePermissions refuse a payload left over from another identity
   * (impersonation start/stop) instead of rendering it.
   */
  userId?: string
  isSystemAdmin: boolean
  permissionGrants: PermissionGrant[]
  catalog: PermissionsCatalog[]
}

export interface UserPreference {
  id: string
  user_id: string
  preference_key: string
  value: string | null
  created_at: string
  updated_at: string
}

interface UpdatePreferenceRequest {
  key: string
  value: string | null
}

interface ProfileData {
  id: string
  name: string
  email: string
  title?: string | null
  image?: string | null
  createdAt?: Date
  updatedAt?: Date
}

interface UpdateProfileRequest {
  name: string
  email: string
  title?: string
  image?: string
}

interface UpdateProfileResponse {
  message: string
  user: ProfileData
}

interface SSOCheckResponse {
  isSSO: boolean
}

export type ResetPreferencesRequest = {
  scope: 'ui-state' | 'table-layouts' | 'table-views'
  /** Omit to reset every table. */
  tableId?: string
}

export const applicationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Bundled auth bootstrap — replaces the system-admin + resolved-permissions
    // + permissions-catalog waterfall. One request, one server-side fan-out.
    getAuthMe: builder.query<AuthMeResponse, void>({
      query: () => '/auth/me',
      providesTags: [
        { type: 'AuthMe', id: 'CURRENT' },
        { type: 'PermissionGrants', id: 'LIST' },
        { type: 'Permissions', id: 'LIST' }
      ]
    }),
    // Read grants by scope
    getPermissionGrantsByScope: builder.query<
      PermissionGrant[],
      { scope: string; scopeId: string; organizationId?: string }
    >({
      query: ({ scope, scopeId, organizationId }) => {
        const base = `/permissions/grants/${scope}/${scopeId}`
        return organizationId
          ? `${base}?organizationId=${organizationId}`
          : base
      },
      providesTags: (result = []) => [
        { type: 'PermissionGrants' as const, id: 'LIST' },
        ...result.map(({ id }) => ({
          type: 'PermissionGrants' as const,
          id
        }))
      ]
    }),

    // 3. Mutation to save grants
    updatePermissionGrants: builder.mutation<
      { message: string },
      {
        scope: string
        scopeId: string
        permissionIds: string[]
        deniedPermissionIds: string[]
        assignedBy: string
        userId: string
      }
    >({
      query: (body) => ({
        url: '/permissions/grants',
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'PermissionGrants', id: 'LIST' }]
    }),
    requestPasswordReset: builder.mutation<
      { message: string },
      { email: string }
    >({
      query: (body) => ({
        url: '/auth/password-reset/request',
        method: 'POST',
        body
      })
    }),
    resetPassword: builder.mutation<
      { message: string },
      { token: string; password: string }
    >({
      query: (body) => ({
        url: '/auth/password-reset/submit',
        method: 'POST',
        body
      })
    }),
    submitFeedback: builder.mutation<
      { success: boolean },
      { title: string; description: string }
    >({
      query: (body) => ({
        url: '/feedback',
        method: 'POST',
        body
      })
    }),
    globalSearch: builder.query<
      GlobalSearchResponse,
      { q: string; scope?: SearchScope; entityScoped?: boolean }
    >({
      query: ({ q, scope, entityScoped }) => ({
        url: '/search/global',
        params: {
          q,
          ...(scope && scope !== 'all' ? { scope } : {}),
          ...(entityScoped ? { entityScoped: 'true' } : {})
        }
      }),
      keepUnusedDataFor: 30
    }),
    getPreferences: builder.query<UserPreference[], void>({
      query: () => '/preferences',
      providesTags: ['Preferences']
    }),
    updatePreference: builder.mutation<UserPreference, UpdatePreferenceRequest>(
      {
        query: (data) => ({
          url: '/preferences',
          method: 'PUT',
          body: data
        }),
        invalidatesTags: ['Preferences']
      }
    ),
    /**
     * Batch write. UI preferences are chatty (a rail collapse, a width drag),
     * so `usePreference` debounces and coalesces into one of these rather than
     * a PUT per key.
     *
     * Optimistically patches the cached list so the control reflects the change
     * immediately — without it, every toggle would visibly wait on the network.
     * A rejected write rolls the cache back and the UI snaps to the real value.
     *
     * Encodes through the CATALOG rather than inline. This used to do its own
     * `typeof value === 'boolean' ? … : String(value)`, which was fine while
     * every preference was a boolean/number/string and silently wrong the moment
     * one held an object: `String({…})` is `"[object Object]"`, which the json
     * codec can't parse, so the value read back as its default until the
     * post-mutation refetch landed. The room-grid preferences are objects.
     */
    patchPreferences: builder.mutation<
      UserPreference[],
      Record<string, unknown>
    >({
      query: (preferences) => ({
        url: '/preferences',
        method: 'PATCH',
        body: { preferences }
      }),
      onQueryStarted: async (preferences, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          applicationApi.util.updateQueryData(
            'getPreferences',
            undefined,
            (draft) => {
              for (const [key, value] of Object.entries(preferences)) {
                // Unknown key: leave the cache alone. The server rejects it, and
                // guessing an encoding for something with no catalog entry is
                // how the [object Object] bug happened.
                if (!isPreferenceKey(key)) continue
                const result = encodePreference(key, value)
                if (!result.ok) continue
                const encoded = result.encoded
                const existing = draft.find((p) => p.preference_key === key)
                if (existing) existing.value = encoded
                else
                  draft.push({
                    id: `optimistic:${key}`,
                    user_id: '',
                    preference_key: key,
                    value: encoded,
                    created_at: '',
                    updated_at: ''
                  })
              }
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: ['Preferences']
    }),
    /**
     * "What have I customized?" — the read model behind /me/preferences.
     * Separate from getPreferences (the raw KV list the app reads at runtime):
     * this one is catalog-joined and carries the table/notification/alert counts,
     * and only the settings page wants it.
     */
    getPreferencesSummary: builder.query<PreferencesSummary, void>({
      query: () => '/me/preferences',
      providesTags: ['Preferences']
    }),
    /**
     * Scoped reset. Invalidates Preferences so the raw KV list refetches too —
     * clearing ui-state changes values the whole app is reading.
     */
    resetPreferences: builder.mutation<
      { ok: true; scope: string; cleared: number },
      ResetPreferencesRequest
    >({
      query: (body) => ({ url: '/me/preferences', method: 'DELETE', body }),
      invalidatesTags: ['Preferences']
    }),
    updateProfile: builder.mutation<
      UpdateProfileResponse,
      UpdateProfileRequest
    >({
      query: (profileData) => ({
        url: '/profile',
        method: 'PUT',
        body: profileData
      })
    }),
    checkSSO: builder.query<SSOCheckResponse, string>({
      query: (userId) => `/profile/auth/check-sso/${userId}`
    })
  })
})

export const {
  useGetAuthMeQuery,
  useGetPermissionGrantsByScopeQuery,
  useUpdatePermissionGrantsMutation,
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
  useSubmitFeedbackMutation,
  useLazyGlobalSearchQuery,
  useGetPreferencesQuery,
  useUpdatePreferenceMutation,
  usePatchPreferencesMutation,
  useGetPreferencesSummaryQuery,
  useResetPreferencesMutation,
  useUpdateProfileMutation,
  useCheckSSOQuery
} = applicationApi
